import React, { useState } from 'react';
import type { RfqRetryView } from './useRfqRetry';
import type { RfqDetailDto, RfqItemDto } from '../../lib/rfqApi';
import { updateRfq, rfqVesselUpdateErrorMessage, rfqClientLinkErrorMessage } from '../../lib/rfqApi';
import { getClients } from '../../lib/clientsApi';
import { useNavigate } from 'react-router-dom';
import type { ReclassifyCategory } from '../../lib/inboxApi';
import { classifyInboxEmail, inboxReclassifyErrorMessage, updateInboxEmailCategory } from '../../lib/inboxApi';
import { RECLASSIFY_OPTIONS, reclassifyCategoriesOf, reclassifyCategoryLabel } from './reclassify';
import { Modal } from '../../components/Modal';
import { AttachmentPreviewModal } from '../../components/AttachmentPreviewModal';
import { UntrustedHtmlFrame } from '../../components/UntrustedHtmlFrame';
import { formatDateTime as fmtDateTime } from "../../utils/dateFormat";
import { opensInBrowser } from '../../utils/attachmentPreview';
import { rfqHeaderFactRows } from '../../utils/rfqHeaderFactRows';
import type { ClientAmendmentDto } from '../../lib/quotationApi';
import { generationErrorText } from './generationError';
import { ClientAmendmentBanner } from '../quotations/ClientAmendmentBanner';

interface Props {
  rfq?: RfqDetailDto | null;
  items?: RfqItemDto[];
  onOpenItemsModal: () => void;
  onUploadRfqClick: () => void;
  onDeleteRfq?: (id: string) => void;
  onEmailReclassified?: () => void;
  // D99.13: retry state is owned by the page (useRfqRetry); this screen only shows it.
  retry?: RfqRetryView;
  onRetry?: (rfq: any) => void;
}

const formatHeaderLabel = (key: string) => {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
};

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return fmtDateTime(d);
};

// ✅ CRA-safe: no process, no import.meta.
function getApiBaseUrl(): string {
  const w = window as any;

  const injected = typeof w.__API_BASE_URL__ === 'string' ? w.__API_BASE_URL__.trim() : '';
  if (injected) return injected.replace(/\/+$/, '');

  const origin = window.location.origin; // e.g. http://localhost:3000
  const isLocal3000 = /localhost:3000$/i.test(origin) || /127\.0\.0\.1:3000$/i.test(origin);

  // ✅ FIX: backend is running on 3001 (per your logs)
  if (isLocal3000) return 'http://localhost:3001';

  // production (backend serves client or reverse-proxy)
  return origin.replace(/\/+$/, '');
}

function buildAbsoluteFileUrl(fileUrl: string): string {
  const raw = String(fileUrl || '').trim();
  if (!raw) return '';

  if (/^https?:\/\//i.test(raw)) return raw;

  const base = getApiBaseUrl();
  const pathClean = raw.startsWith('/') ? raw : `/${raw}`;
  return `${base}${pathClean}`;
}

// ✅ Minimal sanitizer (keeps layout & links, strips scripts/events)
export const RfqDetail: React.FC<Props> = ({
  rfq,
  items,
  onOpenItemsModal,
  onUploadRfqClick,
  onDeleteRfq,
  onEmailReclassified,
  retry,
  onRetry,
}) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'html' | 'text'>('html');
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const [reclassifySaving, setReclassifySaving] = useState(false);
  // '' until the user picks one — see EmailDetail: a move is never a preselected default.
  const [selectedCategory, setSelectedCategory] = useState<ReclassifyCategory | ''>('');
  const [reclassifyError, setReclassifyError] = useState<string | null>(null);
  const [aiClassifying, setAiClassifying] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState<any | null>(null);
  const [previewAtt, setPreviewAtt] = useState<{ url: string; filename?: string; mimeType?: string } | null>(null);

  // Vessel, editable where the RFQ is REVIEWED — which is the earliest point in
  // the chain a person sees the document, and therefore the point where naming
  // the ship still reaches the quotation that has not been generated yet.
  //
  // Extraction now reads a vessel out of the subject or a "Vessel:" line
  // (`utils/vesselName.js`), but it deliberately refuses to guess: a wrong
  // vessel routes goods to the wrong ship, so anything uncertain arrives here
  // blank and waits for a person. This box is that person's way in. Saving it
  // records the value as MANUAL, which no later re-extraction can overwrite.
  const [vesselDraft, setVesselDraft] = useState<string | null>(null);
  const [savingVessel, setSavingVessel] = useState(false);
  const [vesselError, setVesselError] = useState<string | null>(null);
  const [vesselOverride, setVesselOverride] = useState<string | null>(null);

  // D105.13: the way out of "Client not linked" — pick (or create) the client; the server relinks.
  const [clientDraft, setClientDraft] = useState('');
  const [clientNames, setClientNames] = useState<string[]>([]);
  const [linkingClient, setLinkingClient] = useState(false);
  const [clientLinkMsg, setClientLinkMsg] = useState<string | null>(null);
  // undefined = nothing saved here yet; null = the server cleared the error.
  const [customerOverride, setCustomerOverride] = useState<string | null>(null);
  const [linkErrorOverride, setLinkErrorOverride] = useState<string | null | undefined>(undefined);

  // ✅ Always derive these safely BEFORE any early return
  const safeRfq: any = rfq || null;

  // ✅ FIX: support both API shapes:
  // - { rfq: {...}, email: {...} }
  // - { ...rfqFields, email: {...} }
  const rfqObj: any = safeRfq?.rfq || safeRfq;
  const retrying = !!retry?.retrying;
  const ownFailure = retry?.failure ?? null;
  const propGe = rfqObj?.generationError;
  const override = retry?.override ?? null;
  const bannerGe = override ? override.ge : propGe;
  // Mid-retry the server holds the RFQ as 'processing'; after a failed retry the re-read says what it is now.
  const effectiveStatus = override?.status ?? rfqObj?.status;
  const bannerCode: string | undefined = bannerGe?.code;
  const emailBase = safeRfq?.email || rfqObj?.email || {};
  const email = overrideEmail && overrideEmail._id === emailBase?._id ? overrideEmail : emailBase;

  React.useEffect(() => {
    setOverrideEmail(null);
    // A different RFQ is on screen — drop the previous one's edit state, or the
    // saved vessel would appear to belong to the row the user just clicked.
    setVesselOverride(null);
    setVesselDraft(null);
    setVesselError(null);
    setClientDraft('');
    setClientLinkMsg(null);
    setLinkErrorOverride(undefined);
    setCustomerOverride(null);
  }, [rfqObj?._id, emailBase?._id]);

  const clientLinkError: string | null = linkErrorOverride !== undefined ? linkErrorOverride : rfqObj?.clientLinkError || null;

  React.useEffect(() => {
    if (!clientLinkError) return;
    let live = true;
    getClients()
      .then((list) => live && setClientNames(list.map((c) => c.name)))
      // The list is only a suggestion: a 403 (no clients:view) leaves the box free text, anything else is worth a warning.
      .catch((e) => {
        if (e?.status !== 403) console.warn('[RfqDetail] client list failed', e);
      });
    return () => {
      live = false;
    };
  }, [!!clientLinkError]); // eslint-disable-line react-hooks/exhaustive-deps

  const customerName: string = customerOverride ?? String(rfqObj?.customerName || '');
  const clientDraftName = clientDraft.trim();

  const handleLinkClient = async () => {
    if (!rfqObj?._id || !clientDraftName) return;
    setLinkingClient(true);
    setClientLinkMsg(null);
    try {
      // The server finds or creates the client (clientLinkFieldsFor), so nothing is created here.
      const updated: any = await updateRfq(String(rfqObj._id), { customerName: clientDraftName });
      if (updated?.clientLinkError) {
        // The server declined the name (e.g. a junk name): it decides, the box keeps the draft.
        setClientLinkMsg('That name could not be linked to a client.');
        setLinkErrorOverride(String(updated.clientLinkError));
        setCustomerOverride(String(updated?.customerName ?? ''));
        return;
      }
      setLinkErrorOverride(null);
      setCustomerOverride(String(updated?.customerName ?? clientDraftName));
      setClientDraft('');
    } catch (e: unknown) {
      setClientLinkMsg(rfqClientLinkErrorMessage());
    } finally {
      setLinkingClient(false);
    }
  };

  const vesselName: string = vesselOverride ?? String(rfqObj?.vesselName || '');

  const handleSaveVessel = async () => {
    if (!rfqObj?._id || vesselDraft === null) return;
    setSavingVessel(true);
    setVesselError(null);
    try {
      const updated: any = await updateRfq(String(rfqObj._id), { vesselName: vesselDraft });
      // The server is the authority on what was stored — it trims, refuses
      // placeholders like "N/A", and may keep an existing value. Echoing the
      // draft back would show a name that is not in the database.
      setVesselOverride(String(updated?.vesselName ?? ''));
      setVesselDraft(null);
    } catch (e: unknown) {
      setVesselError(rfqVesselUpdateErrorMessage(e));
    } finally {
      setSavingVessel(false);
    }
  };

  const emailSubject: string =
    String(email.subject || '').trim() ||
    String(rfqObj?.emailSubject || '').trim() ||
    String(rfqObj?.subject || '').trim() ||
    '(No subject)';

  const emailHtml: string = email.bodyHtml || email.rawBodyHtml || email.cleanedHtml || '';
  const emailText: string = email.cleanedBody || email.bodyText || email.rawBodyText || '';
  const aiConfidence =
    typeof email?.aiInboxConfidence === 'number'
      ? Math.max(0, Math.min(100, Math.round(email.aiInboxConfidence * 100)))
      : null;

  // ✅ Now it's safe to early return
  if (!rfq) {
    return (
      <div className="flex h-full items-center justify-center bg-[#f7faf8]">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <span className="text-lg text-slate-400">✉️</span>
          </div>
          <div className="text-sm font-semibold text-slate-700">Select an RFQ to view details</div>
          <div className="mt-1 text-xs text-slate-400">Nothing is selected from the inbox.</div>
        </div>
      </div>
    );
  }

  const allItems = items ?? [];
  const hasItems = allItems.length > 0;

  const headerData = (rfqObj?.extractedData && (rfqObj.extractedData as any).header) || {};
  // The buyer's own RFQ number, port, dates and currency, read from the document at intake and stored on
  // the RFQ: the same rows the quotation header shows (utils/rfqHeaderFactRows).
  const factRows = rfqHeaderFactRows(rfqObj);
  const factKeys = new Set(factRows.map((row) => row.key.toLowerCase()));

  // ClickUp 86d430096: the ERROR tag gave no reason on hover. Extraction failures
  // that threw (attachment unreadable, unsupported format, ...) already store the
  // message in extractedData.error (rfq.service.js's catch blocks); a status of
  // 'error' with no stored message means extraction ran but found zero items.
  //
  // D43.6: a document not read in full is not READY either, and says what was not read. Each
  // `readIssues` entry is { code, detail } from the server; `detail` is written for a person.
  const readIssues = (((rfqObj?.extractedData as any)?.readIssues || []) as Array<{ code?: string; detail?: string }>)
    .filter((issue) => issue && issue.detail);
  const errorReason =
    rfqObj?.status === 'error'
      ? (rfqObj.extractedData as any)?.error ||
        (readIssues.length ? readIssues.map((issue) => issue.detail).join(' ') : '') ||
        'No items could be extracted from this document.'
      : undefined;

  // ✅ FIX: do not show "subject" inside header grid (you already show subject at top)
  const headerEntries = Object.entries(headerData || {})
    .filter(([key, value]) => {
      if (value == null) return false;
      if (String(value).trim() === '') return false;

      const k = String(key || '').trim().toLowerCase();
      if (k === 'subject') return false;
      // The extractor's own "port" and "rfqDate" are facts the RFQ now stores once: shown below, not twice.
      if (factKeys.has(k)) return false;

      return true;
    })
    .slice(0, 8)
    .map(([key, value]) => ({ key, label: formatHeaderLabel(key), value: String(value), title: undefined as string | undefined }));
  const panelRows = [
    ...headerEntries,
    ...factRows.map((row) => ({ key: `fact-${row.key}`, label: row.label, value: row.value, title: row.hint as string | undefined })),
  ];

  const attachments = rfqObj?.attachments ?? [];

  const handleDeleteClick = () => {
    if (onDeleteRfq && (rfqObj as any)._id) {
      onDeleteRfq((rfqObj as any)._id);
    }
  };

  const quoteId: string | undefined =
    ((rfqObj as any).quoteId as string | undefined) || ((rfqObj as any).quotationId as string | undefined);

  const canViewQuote = !!quoteId;

  const handleViewQuote = () => {
    if (!quoteId) return;
    navigate(`/quotations/${quoteId}`);
  };

  const handleRetryGeneration = () => {
    if (!retrying) onRetry?.(rfqObj);
  };

  const currentCategories = reclassifyCategoriesOf(email?.inboxTypes);

  const openReclassify = () => {
    if (!email || !email._id) return;
    setSelectedCategory('');
    setReclassifyError(null);
    setReclassifyOpen(true);
  };

  const handleSaveReclassify = async () => {
    if (!email || !email._id || !selectedCategory) return;
    setReclassifySaving(true);
    setReclassifyError(null);
    try {
      // Moves the whole conversation this RFQ's email belongs to (D43.11).
      await updateInboxEmailCategory(email._id, selectedCategory);
      onEmailReclassified?.();
      setReclassifyOpen(false);
    } catch (e: unknown) {
      setReclassifyError(inboxReclassifyErrorMessage(e));
      // The move was saved; only building the RFQ failed — the list must still reflect it.
      if ((e as { code?: unknown } | null)?.code === 'INBOX_RFQ_NOT_CREATED') onEmailReclassified?.();
    } finally {
      setReclassifySaving(false);
    }
  };

  const handleAiClassify = async () => {
    if (!email || !email._id) return;
    setAiClassifying(true);
    try {
      const updated = await classifyInboxEmail(email._id);
      if (String(updated._id) === String(email._id)) setOverrideEmail(updated);
      // A suggestion for the menu, not a move: Save still has to be pressed.
      setSelectedCategory(reclassifyCategoriesOf(updated.inboxTypes)[0] || '');
      onEmailReclassified?.();
    } catch (e: any) {
      window.alert(e?.message || 'Failed to classify email');
    } finally {
      setAiClassifying(false);
    }
  };

  // ✅ Prefer GCS objectPath when available; fallback to legacy fileUrl/storagePath.
  const getAttachmentUrl = (att: any): string => {
    if (!att) return '';

    // 1) If backend provided explicit fileUrl, use it
    const fileUrl = (att?.fileUrl as string | undefined) || '';
    if (fileUrl) return buildAbsoluteFileUrl(fileUrl);

    // 2) If GCS pointer exists, use /files/<objectPath>
    const objectPath =
      String(att?.storage?.objectPath || att?.objectPath || '').trim() ||
      (() => {
        const gsUri = String(att?.storage?.gsUri || att?.gsUri || '').trim();
        if (!gsUri.startsWith('gs://')) return '';
        const rest = gsUri.slice('gs://'.length);
        const idx = rest.indexOf('/');
        if (idx === -1) return '';
        return rest.slice(idx + 1);
      })();

    if (objectPath) {
      const cleaned = objectPath.replace(/^\/+/, '');
      return buildAbsoluteFileUrl(`/files/${cleaned}`);
    }

    // 3) Legacy: /files/<storagePath>
    const sp = String(att?.storagePath || '').trim();
    if (sp) {
      const cleaned = sp.replace(/^\/+/, '');
      return buildAbsoluteFileUrl(`/files/${cleaned}`);
    }

    return '';
  };

  const handleAttachmentClick = (e: React.MouseEvent, att: any) => {
    e.preventDefault();
    const absUrl = getAttachmentUrl(att);
    if (!absUrl) return;
    setPreviewAtt({ url: absUrl, filename: att?.filename, mimeType: att?.mimeType });
  };

  return (
    <div className="h-full bg-white overflow-y-auto p-6">
      <div className="mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div
              className="text-sm font-semibold text-slate-800 truncate"
              title={emailSubject}
              aria-label={emailSubject}
            >
              {emailSubject}
            </div>

            <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-x-1 gap-y-1">
              {customerName && <span>{customerName}</span>}
              {customerName && <span>•</span>}
              {vesselDraft === null ? (
                <>
                  <span>{vesselName || 'No vessel'}</span>
                  <button
                    type="button"
                    onClick={() => { setVesselError(null); setVesselDraft(vesselName); }}
                    className="font-semibold text-primary-500 hover:underline"
                  >
                    {vesselName ? 'Edit' : 'Add vessel'}
                  </button>
                </>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <input
                    autoFocus
                    value={vesselDraft}
                    onChange={(e) => setVesselDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveVessel();
                      if (e.key === 'Escape') { setVesselDraft(null); setVesselError(null); }
                    }}
                    placeholder="e.g. MV NORDIC"
                    className="h-6 w-44 rounded border border-slate-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={handleSaveVessel}
                    disabled={savingVessel}
                    className="h-6 rounded bg-primary-500 px-2 text-xs font-semibold text-white disabled:opacity-60"
                  >
                    {savingVessel ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setVesselDraft(null); setVesselError(null); }}
                    className="h-6 rounded border border-slate-300 px-2 text-xs font-semibold text-slate-600"
                  >
                    Cancel
                  </button>
                </span>
              )}
              {rfqObj.receivedAt && (
                <>
                  <span>•</span>
                  <span>{formatDateTime(rfqObj.receivedAt)}</span>
                </>
              )}
            </div>
            {clientLinkMsg && (
              <div className="mt-1 text-xs text-rose-600" role="alert">{clientLinkMsg}</div>
            )}
            {vesselError && (
              <div className="mt-1 text-xs text-rose-600" role="alert">{vesselError}</div>
            )}

            {email.from && (
              <div className="mt-1 text-xs text-slate-500">
                <span className="font-semibold">From:</span> <span>{email.from}</span>
                {email.to && (
                  <>
                    <span className="mx-1">|</span>
                    <span className="font-semibold">To:</span> <span>{email.to}</span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              {aiConfidence != null && (
                <span className="px-2 py-0.5 rounded-full border text-xs font-medium bg-cyan-50 text-cyan-700 border-cyan-200">
                  AI {aiConfidence}%
                </span>
              )}
              <span
                className={`px-2 py-0.5 rounded-full border text-xs font-medium ${
                  rfqObj.status === 'processed'
                    ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                    : rfqObj.status === 'ready'
                    ? 'bg-sky-50 text-sky-700 border-sky-200'
                    : rfqObj.status === 'pending'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : rfqObj.status === 'error'
                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                    : 'bg-slate-50 text-slate-600 border-slate-200'
                }`}
                title={errorReason}
              >
                {rfqObj.status?.toString().toUpperCase()}
              </span>

              {(rfqObj as any).badge && (
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs font-semibold text-slate-600 whitespace-nowrap">
                  {(rfqObj as any).badge}
                </span>
              )}

              {clientLinkError && (
                <span
                  className="px-2 py-0.5 rounded-full bg-red-100 text-xs font-semibold text-red-700 whitespace-nowrap"
                  title={clientLinkError}
                >
                  ⚠ Client not linked — add manually
                </span>
              )}
              {clientLinkError && (
                <span className="flex items-center gap-1">
                  <input
                    aria-label="Client"
                    list="rfq-client-names"
                    value={clientDraft}
                    onChange={(e) => setClientDraft(e.target.value)}
                    placeholder="Choose client"
                    className="w-40 rounded-md border border-slate-200 px-2 py-0.5 text-xs"
                  />
                  <datalist id="rfq-client-names">
                    {clientNames.map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                  <button
                    type="button"
                    onClick={handleLinkClient}
                    disabled={linkingClient || !clientDraftName}
                    className="rounded-md border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-xs font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-50 whitespace-nowrap"
                  >
                    Link client
                  </button>
                </span>
              )}
            </div>

            {canViewQuote && (
              <button
                type="button"
                onClick={handleViewQuote}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 whitespace-nowrap leading-none"
                title="Open generated quotation"
              >
                <span className="text-xs leading-none">📄</span>
                <span className="leading-none">View Quote</span>
              </button>
            )}

            {email?._id && (
              <button
                type="button"
                onClick={openReclassify}
                className="inline-flex items-center gap-1 rounded-md border border-cyan-200 bg-cyan-50 px-2 py-1 text-xs font-semibold text-cyan-700 hover:bg-cyan-100 whitespace-nowrap leading-none"
              >
                Reclassify
              </button>
            )}

            {onDeleteRfq && (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 whitespace-nowrap leading-none"
              >
                <span className="text-xs leading-none">🗑️</span>
                <span className="leading-none">Delete</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {bannerCode && (
        <div role="alert" className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          A quotation could not be generated: {generationErrorText(bannerCode)}
          {bannerGe?.at ? ` (${fmtDateTime(bannerGe.at)})` : ''}
          {(effectiveStatus === 'error' || retrying) && (
            <div className="mt-2">
              <button
                type="button"
                onClick={handleRetryGeneration}
                disabled={retrying}
                className="rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
              >
                {retrying ? 'Retrying…' : 'Retry generation'}
              </button>
            </div>
          )}
        </div>
      )}
      {/* The request's own outcome, shown even when the RFQ no longer stores a failure (someone else fixed it). */}
      {ownFailure && (
        <div role="status" className="mb-4 text-xs text-rose-800">
          Retry failed: {ownFailure.code ? generationErrorText(ownFailure.code) : 'the server could not be reached'}
        </div>
      )}

      {/* D45.1: changes the client asked for by email, applied to this RFQ's quotation (or a new
          version of it) and not yet reviewed. Which ones are waiting is the server's answer. */}
      {Array.isArray(rfqObj?.pendingClientAmendments) && rfqObj.pendingClientAmendments.length > 0 && (
        <div className="mb-4">
          <ClientAmendmentBanner
            amendments={rfqObj.pendingClientAmendments as ClientAmendmentDto[]}
            onOpenQuotation={(quotationId) => navigate(`/quotations/${quotationId}`)}
          />
        </div>
      )}

      <Modal
        isOpen={reclassifyOpen}
        onClose={() => setReclassifyOpen(false)}
        title="Reclassify conversation"
        maxWidthClass="max-w-md"
      >
        <div className="space-y-4">
          <div className="text-xs text-slate-600">
            Currently in:{' '}
            <span className="font-semibold">
              {currentCategories.length ? currentCategories.map(reclassifyCategoryLabel).join(', ') : 'no category'}
            </span>
          </div>
          <div>
            <label htmlFor="rfq-reclassify-category" className="block text-xs font-semibold text-slate-700 mb-1">
              Move the conversation to
            </label>
            <select
              id="rfq-reclassify-category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as ReclassifyCategory | '')}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="" disabled>
                Choose a category
              </option>
              {RECLASSIFY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Every email the client sent in this conversation moves with it.
            </p>
          </div>
          {reclassifyError && (
            <div className="text-xs text-rose-600" role="alert">
              {reclassifyError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleAiClassify}
              disabled={aiClassifying}
              className="px-3 py-2 rounded-md border border-cyan-200 text-xs font-semibold text-cyan-700 hover:bg-cyan-50 disabled:opacity-60"
            >
              {aiClassifying ? 'Classifying...' : 'Classify with AI'}
            </button>
            <button
              onClick={() => setReclassifyOpen(false)}
              className="px-3 py-2 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveReclassify}
              disabled={reclassifySaving || !selectedCategory}
              className="px-3 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-60"
            >
              {reclassifySaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      {panelRows.length > 0 && (
        <div className="mb-4">
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 p-3 bg-[#f7faf8] border border-slate-200 rounded">
            {panelRows.map((row) => (
              <div key={row.key} className="text-xs flex">
                <span className="min-w-[90px] font-semibold text-slate-600" title={row.title}>
                  {row.label}:
                </span>
                <span className="ml-1 text-slate-700 break-all">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center mb-2 gap-1 text-xs font-semibold text-slate-700">
            <span className="text-sm">📎</span>
            <span>Attachments</span>
          </div>
          <div className="border border-slate-200 rounded bg-[#f7faf8] px-3 py-2 space-y-1">
            {attachments.map((att: any, idx: number) => {
              const absUrl = getAttachmentUrl(att);
              const inline = opensInBrowser(att?.filename, att?.mimeType);

              return (
                <div
                  key={`${att.filename}-${idx}`}
                  className="flex items-center justify-between text-xs text-slate-700"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs flex-shrink-0">•</span>
                    <span className="truncate">{att.filename}</span>
                  </div>

                  <div className="ml-2 flex items-center gap-2 text-xs text-slate-500 flex-shrink-0">
                    {att.mimeType && <span>{att.mimeType}</span>}
                    {typeof att.size === 'number' && <span>{(att.size / 1024).toFixed(1)} KB</span>}

                    {absUrl && (
                      <a
                        href={absUrl}
                        target={inline ? '_blank' : undefined}
                        rel={inline ? 'noreferrer' : undefined}
                        download={!inline ? (att.filename || 'attachment') : undefined}
                        onClick={(e) => handleAttachmentClick(e, att)}
                        className="text-primary-500 font-semibold hover:underline whitespace-nowrap"
                        title={inline ? 'Open in new tab' : 'Download'}
                      >
                        {inline ? 'View' : 'Download'}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-semibold text-slate-600">Email body</div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setViewMode('html')}
              className={`px-2 py-0.5 rounded border text-xs font-semibold ${
                viewMode === 'html'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              HTML
            </button>
            <button
              type="button"
              onClick={() => setViewMode('text')}
              className={`px-2 py-0.5 rounded border text-xs font-semibold ${
                viewMode === 'text'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              Text
            </button>
          </div>
        </div>

        {viewMode === 'html' && emailHtml ? (
          <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 leading-relaxed overflow-x-auto">
            <UntrustedHtmlFrame html={emailHtml} title="Email message body" />
          </div>
        ) : (
          <div className="rounded border border-slate-200 bg-[#fdfdfd] px-3 py-2 text-xs text-slate-700 whitespace-pre-line leading-relaxed">
            {emailText || 'No email body available.'}
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 pt-4 mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-700">RFQ Items</div>
          {hasItems && (
            <button
              onClick={onOpenItemsModal}
              className="text-xs text-primary-500 font-semibold hover:underline whitespace-nowrap"
            >
              Open RFQ
            </button>
          )}
        </div>

        {!hasItems ? (
          <div className="p-4 border border-dashed border-amber-300 rounded-md bg-amber-50 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-semibold text-amber-800">Failed to process RFQ automatically</div>
              <div className="text-xs text-amber-700">
                Please upload the RFQ document to extract line items.
              </div>
            </div>
            <button
              onClick={onUploadRfqClick}
              className="px-4 py-1.5 rounded-md bg-primary-500 text-white text-xs font-semibold whitespace-nowrap"
            >
              Upload RFQ
            </button>
          </div>
        ) : (
          <>
            {/* The row account. Rows the parser could not read are surfaced here AND kept as
                needs-review lines in the table below — the file's row count and the RFQ's line
                count have to agree, or the difference has to be on screen. (QA Ticket D,
                ClickUp 86d4a4u10) */}
            {(() => {
              const skipped = ((rfqObj.extractedData as any)?.skippedRows || []) as Array<{ row?: number; reason?: string; cells?: string }>;
              const stats = (rfqObj.extractedData as any)?.rowStats as
                | { dataRows?: number; items?: number; skipped?: number; excluded?: number; unaccounted?: number }
                | undefined;
              const unaccounted = Number(stats?.unaccounted || 0);
              if (!skipped.length && !unaccounted && !readIssues.length) return null;
              return (
                <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {readIssues.length > 0 && (
                    <div className="mb-1">
                      <div className="font-semibold">Not everything was read, so this RFQ is not marked ready:</div>
                      <ul className="mt-0.5 list-disc pl-4 space-y-0.5">
                        {readIssues.map((issue, i) => (
                          <li key={`${issue.code || 'issue'}-${i}`}>{issue.detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {stats?.dataRows != null && (
                    <div className="mb-1">
                      The file held <strong>{stats.dataRows}</strong> row(s): {stats.items} read as items,{' '}
                      {stats.skipped} kept for review, {stats.excluded} read as totals/footers
                      {unaccounted > 0 && <strong> — {unaccounted} unaccounted for</strong>}.
                    </div>
                  )}
                  {skipped.length > 0 && (
                    <div className="font-semibold">{skipped.length} row(s) could not be parsed and are marked NEEDS REVIEW below:</div>
                  )}
                  <ul className="mt-1 list-disc pl-4 space-y-0.5 max-h-32 overflow-auto">
                    {skipped.slice(0, 50).map((s, i) => (
                      <li key={i}>
                        {s.row != null ? `Row ${s.row}: ` : ''}{s.reason || 'unparseable'}
                        {s.cells ? ` — "${s.cells}"` : ''}
                      </li>
                    ))}
                    {skipped.length > 50 && <li>…and {skipped.length - 50} more</li>}
                  </ul>
                </div>
              );
            })()}
          <div className="overflow-auto border border-slate-200 rounded">
            {/* The client's lines as sent: Item, Description, Quantity, Unit only
                (USC-BUG-0044). Split/pack review fields live in the Items modal.
                Item and description stay put if the pane scrolls sideways
                (`frozen-cols-*`, index.css); 48px is the first column's `w-12`. */}
            <table
              className="w-full border-collapse text-xs frozen-cols-2"
              style={{ '--frozen-col-2': '48px' } as React.CSSProperties}
            >
              <thead className="bg-[#f7faf8]">
                <tr className="text-left border-b border-slate-200">
                  <th className="py-1.5 px-2 w-12 bg-[#f7faf8]">Item</th>
                  <th className="py-1.5 px-2 bg-[#f7faf8]">Description</th>
                  <th className="py-1.5 px-2 w-20 text-right">Quantity</th>
                  <th className="py-1.5 px-2 w-16">Unit</th>
                </tr>
              </thead>
              <tbody>
                {allItems.map((it) => (
                  <tr
                    key={it._id}
                    className={`border-b border-slate-100 last:border-0 ${it.needsReview ? 'bg-amber-50' : 'bg-white'}`}
                  >
                    <td className="py-1.5 px-2 w-12 bg-inherit text-slate-600">{it.itemId}</td>
                    <td className="py-1.5 px-2 bg-inherit text-slate-700">
                      {it.description}
                      {/* The row came out of the file unreadable and was KEPT rather than
                          dropped, so it has to say so where the line is read — the
                          description here is the raw source row. (ClickUp 86d4a4u10) */}
                      {it.needsReview && (
                        <span
                          className="ml-2 inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800"
                          title={it.reviewReason || 'This row could not be parsed — check it against the attachment.'}
                        >
                          Needs review
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-700">{it.quantity ?? ''}</td>
                    <td className="py-1.5 px-2 text-slate-700">{it.uom ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {previewAtt && (
        <AttachmentPreviewModal
          url={previewAtt.url}
          filename={previewAtt.filename}
          mimeType={previewAtt.mimeType}
          onClose={() => setPreviewAtt(null)}
        />
      )}
    </div>
  );
};
